import { Stack } from "@mui/material";
import { useMemo, useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import { useGridDataSourceWithRange } from "@entrilia/shared/components/grid/gridHooks";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import { logError } from "@entrilia/shared/logging";
import { downloadFileFromUrl } from "@entrilia/shared/services/downloadFile";
import { formatDate } from "@entrilia/shared/utilities/dateUtils";
import { formatNumber } from "@entrilia/shared/utilities/formatters";
import adminApi from "../../../../../api/adminApi";
import useClientContext from "../../../../../context/useClientContext";
import ExportWithFiltersButton from "../../../../common/ExportWithFiltersButton";
import { FilterContextProvider } from "../../../../common/filters/FilterContextProvider";
import FiltersPanel from "../../../../common/filters/FiltersPanel";
import { getInitialFilterState } from "../../../../common/filters/filterState";
import {
  filterDefinitions,
  getSearchDocumentViewsFiltersPayload,
} from "../../document-activity/documentViewsFilterDefinitions";
import DocumentViewsGrid from "../../document-activity/DocumentViewsGrid";
import { getSelectOptionsForDocumentViews } from "../../document-activity/documentViewsSelectOptionsProvider";
import useFundraisingDetailsPageContext from "./useFundraisingDetailsPageContext";

const exportDocumentViewsToExcel = withErrorHandling(adminApi.exportDocumentViewsToExcel);

const FundraisingDocumentActivity = () => {
  const { clientCode } = useClientContext();
  const { sendNotification, sendNotificationError } = useNotificationContext();
  const { fundraising } = useFundraisingDetailsPageContext();

  const filterId = useMemo(() => `${clientCode}_fundraising_document_activity_filter_v1`, [clientCode]);

  const [filterState, setFilterState] = useState(() => getInitialFilterState(filterId, filterDefinitions, []));

  const filterPayload = useMemo(
    () => getSearchDocumentViewsFiltersPayload(filterState, fundraising.id),
    [filterState, fundraising.id]
  );

  const [isExporting, setIsExporting] = useState(false);

  const { gridApiRef, dataSource, totalRows, isLoading, isRefreshing, refreshDataSource } = useGridDataSourceWithRange(
    adminApi.searchDocumentViews,
    filterPayload
  );

  const handleExport = async (withFilters: boolean) => {
    const fileName = `${fundraising.name}_Document_Activity_${formatDate(new Date(), "yyyyMMdd_HHmmss")}.xlsx`;

    setIsExporting(true);

    const filters = withFilters ? filterPayload : {};
    const [resp, error] = await exportDocumentViewsToExcel(
      { range: {}, ...filters, fundraisingId: fundraising.id },
      fileName
    );

    setIsExporting(false);

    if (error) {
      sendNotificationError(error.message);
      logError(error, "[FundraisingDocumentActivity] exportDocumentViewsToExcel");
      return;
    }

    await downloadFileFromUrl(resp.downloadUrl);

    if (resp.isTruncated) {
      sendNotification(
        `Exported first ${formatNumber(resp.exportedRecordsCount)} records. Please use filters to reduce the dataset and export additional records.`
      );
    }
  };

  return (
    <Stack spacing={2.5} sx={{ py: 2.5, flex: 1 }}>
      <FilterContextProvider
        filterState={filterState}
        setFilterState={setFilterState}
        getSelectOptionsForFilter={getSelectOptionsForDocumentViews}
      >
        <FiltersPanel
          recordCount={totalRows ?? 0}
          isLoading={isLoading}
          isRefreshing={isRefreshing}
          onRefresh={refreshDataSource}
          withoutSearch
        >
          <ExportWithFiltersButton
            disabled={!totalRows || isLoading}
            loading={isExporting}
            onExportAllRecords={() => handleExport(false)}
            onExportFilteredRecords={() => handleExport(true)}
          />
        </FiltersPanel>
      </FilterContextProvider>
      <DocumentViewsGrid apiRef={gridApiRef} dataSource={dataSource} filterId={filterId} />
    </Stack>
  );
};

export default FundraisingDocumentActivity;
